import React from 'react'
import styled from 'styled-components'
import { Notification } from '../../../../data/messaging/data-types'
import { StatusEnum } from '../../../../data/book/data-types'
import { useHistory } from 'react-router-dom'
import { useDialog } from '../../dialog/hook'
import BookDialog from '../../custom-dialogs/book'
import ColoredDialogBackdrop from '../../custom-dialogs/colored-dialog-backdrop'
import ProfileDialog from '../../custom-dialogs/profile'

type NotificationItemProps = {
  notification: Notification
}

const NotificationItem: React.FunctionComponent<NotificationItemProps> = ({ notification }) => {
    const history = useHistory()
    const { setComponent } = useDialog()
    
    const defaultImage = require('../images/default.svg')
    const onClick = () => {
        if (notification.club_id) {
            history.push(`/messages/club/cafe/${notification.club_id}`)
            return
        }

        if (notification.book?.id) {
            setComponent(<BookDialog book={notification.book}/>, <ColoredDialogBackdrop bg={notification.book.bg_color}/>)
            return
        }

        if (notification.notification_type === 'following') {
            setComponent(<ProfileDialog publicId={notification.who_initiated.public_id}/>)
            return
        }
    }

    return (
        <Container is_tapped={notification.is_tapped} onClick={onClick}>
            <InitiatorImage src={notification.who_initiated.picture || defaultImage }/>
            <Body>
                <NotificationText>
                    { notificationText(notification) }
                </NotificationText>
                <NotificationFooter>
                    <Timestamp>
                        { 
                            new Date(notification.creation_date).toLocaleString(
                                navigator.language, 
                                { month: "short", day: "numeric", year: "numeric", hour12: false, hour: "numeric", minute: "numeric" }
                            ) 
                        }
                    </Timestamp>
                    {
                        notification.user_list &&
                        <UserList>
                            {notification.user_list.map(u => <UserImage key={u.id} src={ u.picture || defaultImage }/>)}
                        </UserList>
                    }
                </NotificationFooter>
            </Body>
            { notification.rating 
                ? <RatingContainer>
                    <Star src={require('../images/star.svg')}/>
                    <Rating>{ notification.rating }</Rating>
                </RatingContainer>
                : <div></div>
            }
            { notification.book?.image 
                ? <BookImage 
                    src={notification.book?.image}
                    alt={notification.book?.title}
                    onClick={(ev) => {
                        ev.stopPropagation()
                        setComponent(<BookDialog book={notification.book!}/>, <ColoredDialogBackdrop bg={notification.book!.bg_color}/>)
                    }}
                /> 
                : <div></div> 
            }
        </Container>
    )
}

const notificationText = (notification: Notification) => {
    switch (notification.notification_type) {
        case "connect":
            return [
                <Bold key={1}>{ `${notification.who_initiated.first_name} ${notification.who_initiated.last_name}` }</Bold>,
                <span key={2}> joined </span>,
                <Bold key={3}>{notification.book?.title}</Bold>,
                <span key={4}> - </span>,
                <Bold key={5}><Colored status_id={notification.status_id}>{getStatusText(notification.status_id)}</Colored></Bold>
            ]
        case "book_review":
            return [
                <Bold key={1}>{ `${notification.who_initiated.first_name} ${notification.who_initiated.last_name}` }</Bold>,
                <span key={2}> has left a review for </span>,
                <Bold key={3}>{notification.book?.title}</Bold>,
            ]
        case "following":
            return [
                <Bold key={1}>{ `${notification.who_initiated.first_name} ${notification.who_initiated.last_name}` }</Bold>,
                <span key={2}> is now following you!</span>,
            ]
        default:
            return null
    }
}

const getStatusText = (status_id?: StatusEnum) => {
    switch (status_id) {
        case StatusEnum.WantToRead:
            return "Want to read"
        case StatusEnum.CurrentlyReading:
            return "Currently reading"
        case StatusEnum.Read:
            return "Read"
        default:
            return ""
    }
}

const Container = styled.div<{ is_tapped?: boolean }>`
  display: grid;
  grid-template-columns: auto 1fr auto auto;
  grid-column-gap: 24px;
  align-items: center;
  
  width: 100%;

  padding: 24px;

  border-bottom: 1px solid ${p => p.theme.colors.lightGray };
  background: ${p => p.is_tapped ? p.theme.colors.bg : "rgba(177, 163, 246, 0.1)"};

  font-size: 14px;
  line-height: 24px;

  cursor: pointer;
`

const InitiatorImage = styled.img`
    width: 48px;
    height: 48px;

    border-radius: 50%;
`

const Body = styled.div`
    display: grid;
    grid-template-columns: 1fr;
    grid-row-gap: 5px;
`

const NotificationText = styled.div`
    display: block;
`

const NotificationFooter = styled.div`
    width: 100%;

    display: flex;
    align-items: center;
    grid-template-rows: 1fr;
`

const Timestamp = styled.div`
    display: grid;

    font-size: 14px;
    line-height: 20px;
    color: ${p => p.theme.colors.serviceGray};
`

const UserList = styled.div`
    display: flex;
    margin-left: 30px;
`

const UserImage = styled.img`
    display: grid;

    width: 24px;
    height: 24px;

    border-radius: 50%;
    margin: 0 5px;
`

const RatingContainer = styled.div`
    display: grid;
`

const Star = styled.img`
    width: 20px;
    height: 20px;
`

const Rating = styled.div`
    font-size: 14px;
    line-hieght: 24px;
`

const BookImage = styled.img`
    width: 64px;
    border-radius: 10px;
`

const Bold = styled.span`
    display: inline-block;
    font-weight: bold;
`

const Colored = styled.span<{ status_id?: number }>`
    display: inline-block;
    color: ${p => p.theme.statuses[p.status_id || 0]};
`

export default NotificationItem